import * as R from 'remeda';
import { isValid } from 'date-fns';
import {
  AutomationTarget,
  ProjectionInsights,
  SaveAutomationTargetPayload,
} from '@rossum/api-client/queues';
import {
  isSchemaSection,
  isSchemaSimpleMultiValue,
  isSchemaTableMultiValue,
  isSchemaTuple,
  SchemaItem,
  SchemaSection,
} from '@rossum/api-client/schemas';
import { GridRowIdGetter } from '@rossum/ui/x-data-grid-pro';
import { formatNumberToLocale } from '../../../../lib/helpers';
import { OptimizationData } from './types';

export const unifyDecimalSeparator = (val: string) => val.replace(',', '.');

export const convertErrorRateToPrecisionTarget = (errorRate: number) =>
  1 - errorRate;

export const createOptimizationSettings = ({
  data,
  customPrecision,
  type,
}: {
  data: OptimizationData;
  customPrecision: Record<string, number>;
  type: 'legacy_thresholds' | 'automation_assistant_v1';
}): SaveAutomationTargetPayload => {
  return {
    automationRateTarget: data.automationRate,
    // without rounding JS IEEE-754 floating point mechanism ends us up with 10< decimal numbers
    errorRateTarget: R.round(1 - data.precisionTarget, 4),
    datapointAutomationTargets: data.datapointStatistics.map(dp => {
      const precisionTarget = customPrecision[dp.schemaId];

      // without rounding JS IEEE-754 floating point mechanism ends us up with 10< decimal numbers
      const errorRateLimit = precisionTarget
        ? R.round(1 - precisionTarget, 4)
        : null;

      return {
        schemaId: dp.schemaId,
        errorRateTarget: dp.estimatedErrorRate ?? 0,
        errorRateLimit,
        confidenceThreshold: dp.confidenceThreshold ?? 0,
      };
    }),
    type,
  };
};

/**
 * A function that replaces the confidence thresholds of the optimization object using a defined threshold map
 * @param optimizationObject - The optimization object to replace the confidence thresholds of
 * @param thresholdMap - The threshold map to use for replacing the confidence thresholds
 * @example to replace the confidence thresholds of currentState (coming from RIR) with the existing thresholds in queue schema
 */
const takeThresholdsFromMap = ({
  optimizationObject,
  thresholdMap,
  fallbackThreshold,
}: {
  optimizationObject: OptimizationData;
  thresholdMap: Record<string, number | null>;
  fallbackThreshold: number;
}) => ({
  ...optimizationObject,
  datapointStatistics: optimizationObject.datapointStatistics.map(dp => {
    const confidenceThreshold = thresholdMap[dp.schemaId] ?? fallbackThreshold;
    return {
      ...dp,
      confidenceThreshold,
    };
  }),
});

export const getLegacyThresholdsToSave = ({
  currentState,
  thresholdMapFromSchema,
  defaultScoreThreshold,
}: {
  currentState: OptimizationData;
  thresholdMapFromSchema: Record<string, number | null>;
  defaultScoreThreshold: number;
}) => {
  const legacyData = takeThresholdsFromMap({
    optimizationObject: currentState,
    thresholdMap: thresholdMapFromSchema,
    fallbackThreshold: defaultScoreThreshold,
  });
  return createOptimizationSettings({
    data: legacyData,
    customPrecision: {},
    type: 'legacy_thresholds',
  });
};

export const convertBaselineToOptimizationData = (
  baseline: ProjectionInsights
) => ({
  automationRate: baseline.documentAutomationRate,
  precisionTarget: convertErrorRateToPrecisionTarget(
    baseline.estimatedErrorRate
  ),
  datapointStatistics: baseline.datapointStatistics,
});

export const getLatestSavedAutomationTarget = (
  automationTargets: AutomationTarget[] | undefined
) =>
  automationTargets
    ? R.pipe(
        automationTargets,
        R.filter(target => isValid(new Date(target.datetime))),
        R.sort(
          (a, b) =>
            new Date(b.datetime).getTime() - new Date(a.datetime).getTime()
        ),
        R.first()
      )
    : undefined;

export const getSchemaThresholdMap = (
  schemaContent: SchemaSection[]
): Record<string, number | null> => {
  const result: Record<string, number | null> = {};

  const processItem = (item: SchemaSection | SchemaItem): void => {
    if (isSchemaSection(item)) {
      item.children.forEach(processItem);
      return;
    }

    if (isSchemaTableMultiValue(item)) {
      item.children.children.forEach(processItem);
      return;
    }

    if (isSchemaSimpleMultiValue(item)) {
      if (item.children.type !== 'button') {
        result[item.children.id] = item.children.scoreThreshold ?? null;
      }
      return;
    }

    if (isSchemaTuple(item)) {
      item.children.forEach(processItem);
      return;
    }

    if (item.type !== 'button') {
      result[item.id] = item.scoreThreshold ?? null;
    }
  };

  schemaContent.forEach(processItem);
  return result;
};

export const updateSchemaThresholds = (
  data: SchemaSection[],
  thresholdMap: Record<string, number | null>
): SchemaSection[] => {
  const updateItem = <T extends SchemaSection | SchemaItem>(item: T): T => {
    if (isSchemaSection(item)) {
      return {
        ...item,
        children: item.children.map(updateItem),
      };
    }

    if (isSchemaTableMultiValue(item)) {
      return {
        ...item,
        children: {
          ...item.children,
          children: item.children.children.map(updateItem),
        },
      };
    }

    if (isSchemaSimpleMultiValue(item)) {
      const newScoreThreshold = thresholdMap[item.children.id];

      return item.children.type !== 'button' &&
        R.isNonNullish(newScoreThreshold)
        ? {
            ...item,
            children: { ...item.children, scoreThreshold: newScoreThreshold },
          }
        : item;
    }

    if (isSchemaTuple(item)) {
      return {
        ...item,
        children: item.children.map(updateItem),
      };
    }

    const newScoreThreshold = thresholdMap[item.id];

    return item.type !== 'button' && R.isNonNullish(newScoreThreshold)
      ? {
          ...item,
          scoreThreshold: newScoreThreshold,
        }
      : item;
  };

  return data.map(updateItem);
};

export const convertRateToPercentage = (
  value: number | undefined,
  options?: { padding?: number }
) => {
  if (value === undefined) return EMPTY_PLACEHOLDER;
  const { padding = 2 } = options || {};
  const floored = value * 100;
  return `${formatNumberToLocale(floored, undefined, { maximumFractionDigits: padding, minimumFractionDigits: padding })}%`;
};

export const getRowId: GridRowIdGetter<{ schemaId: string }> = row =>
  row.schemaId;

export const EMPTY_PLACEHOLDER = '-';

// a finite number to enable quality spot check feature on a queue.
// Quality Spot Check: The percentage of randomly chosen documents to require a human confirmation.
export const QUALITY_SPOT_CHECK_PERCENTAGE = 0.02;
